/**
 * ModeSwitcher · v2.7.0 batch DO+
 * ================================
 * Header-level dropdown that lets multi-role users flip between
 * Admin Console · Driver App · Rider Portal · Developer area without logging
 * out. Backed by the existing token-mint bridges:
 *   • /admin/staff/rider-mode  → mints customer JWT (admin → /ride)
 *   • /admin/staff/driver-mode → mints driver JWT   (admin → /dashboard)
 *   • /driver/staff/dispatch-mode → mints admin JWT (driver → /admin)
 *
 * Context detection:
 *   • mode="admin"    → uses adminApi (token in rtg_admin_token)
 *   • mode="driver"   → uses api      (token in rtg_token)
 *   • mode="rider"    → uses customerApi (token in rtg_customer_token)
 *
 * The rider-side header ONLY shows the switcher when the rider's email is
 * linked to a driver/admin row (verified via /api/customer/me/roles which
 * always returns 200).
 */
import { useEffect, useState } from "react";
import { ChevronDown, Car, Shield, User, Code2, Loader2, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";

import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

import adminApi from "../lib/adminApi";
import api from "../lib/api";
import customerApi from "../lib/customerApi";

// --- Persist last-used mode so we can highlight + restore later ---
const LAST_MODE_KEY = "rtg_last_mode";

function setLastMode(mode) {
  try { localStorage.setItem(LAST_MODE_KEY, mode); } catch { /* no-op */ }
}

// ----------------------------------------------------------------------------
// useAvailableModes()
// Returns which destinations the calling user can hop to from the current mode.
// ----------------------------------------------------------------------------
function useAvailableModes(mode) {
  const [state, setState] = useState({ loading: true, roles: [], can: {} });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        let data;
        if (mode === "admin") {
          ({ data } = await adminApi.get("/admin/me/roles"));
        } else if (mode === "driver") {
          // Driver-side endpoint piggybacks on the same /admin/me/roles
          // by introspecting JWT (kind=driver) — but to avoid backend changes
          // we instead infer from the local user payload.
          const user = JSON.parse(localStorage.getItem("rtg_user") || "{}");
          const role = (user.role || "driver").toLowerCase();
          const rs = Array.from(new Set([role, ...(user.roles || [])])).filter(Boolean);
          data = {
            roles: rs,
            is_developer: rs.includes("developer"),
            is_operating_partner: rs.includes("operating_partner") || rs.includes("supervisor"),
            is_super: rs.some((r) => ["operating_partner", "developer", "supervisor", "admin"].includes(r)),
          };
        } else if (mode === "rider") {
          ({ data } = await customerApi.get("/customer/me/roles"));
        }
        if (!active) return;
        const roles = data?.roles || [];
        const isAdmin = roles.some((r) =>
          ["operating_partner", "developer", "dispatcher", "team_leader", "field_supervisor", "supervisor", "admin"].includes(r)
        );
        const isDriver = roles.includes("driver") || isAdmin;
        const isRider = true; // anyone can flip into rider mode
        setState({
          loading: false,
          roles,
          can: {
            admin: mode !== "admin" && isAdmin,
            driver: mode !== "driver" && isDriver,
            rider: mode !== "rider" && isRider,
            developer: roles.includes("developer"),
          },
        });
      } catch {
        if (active) setState({ loading: false, roles: [], can: {} });
      }
    })();
    return () => { active = false; };
  }, [mode]);

  return state;
}

// ----------------------------------------------------------------------------
// Inner helper — switches the active app by minting + storing the right token.
// Opens in same tab so the experience reads like "switching modes", not
// "opening another app".
// ----------------------------------------------------------------------------
async function jumpTo(mode, target, setBusy) {
  setBusy(target);
  try {
    if (mode === "admin" && target === "rider") {
      const { data } = await adminApi.post("/admin/staff/rider-mode");
      localStorage.setItem("rtg_customer_token", data.access_token);
      localStorage.setItem("rtg_customer", JSON.stringify(data.customer));
      setLastMode("rider");
      window.location.href = "/ride";
      return;
    }
    if (mode === "admin" && target === "driver") {
      const { data } = await adminApi.post("/admin/staff/driver-mode");
      localStorage.setItem("rtg_token", data.access_token);
      // v2.7.0 batch DO++++++ · AuthContext reads `rtg_driver`, not
      // `rtg_user`. Persist BOTH so legacy and current consumers stay happy.
      localStorage.setItem("rtg_driver", JSON.stringify(data.driver));
      localStorage.setItem("rtg_user", JSON.stringify(data.driver));
      setLastMode("driver");
      window.location.href = "/dashboard";
      return;
    }
    if (mode === "driver" && target === "admin") {
      const { data } = await api.post("/driver/staff/dispatch-mode");
      localStorage.setItem("rtg_admin_token", data.access_token);
      localStorage.setItem("rtg_admin", JSON.stringify(data.admin));
      setLastMode("admin");
      window.location.href = "/admin";
      return;
    }
    if (mode === "driver" && target === "rider") {
      // v2.8 · Driver → Rider mints a customer session DIRECTLY so ANY driver
      // (including regular drivers) can "ride as myself" — the old chain went
      // through the elevated-only dispatch-mode and 403'd for regular drivers.
      const { data } = await api.post("/driver/rider-mode");
      localStorage.setItem("rtg_customer_token", data.access_token);
      localStorage.setItem("rtg_customer", JSON.stringify(data.customer));
      setLastMode("rider");
      window.location.href = "/ride";
      return;
    }
    if (mode === "rider" && (target === "admin" || target === "driver")) {
      // Rider → Admin/Driver requires the rider to have an existing dispatch
      // or driver session token already (set via the linked driver login).
      // Best UX: prompt for re-login on the appropriate surface.
      toast.message("Sign in with your dispatch account to continue", {
        description: "For security, hopping into Admin or Driver requires a fresh sign-in.",
      });
      window.location.href = target === "admin" ? "/admin/login" : "/login";
      return;
    }
    if (target === "developer") {
      setLastMode("admin");
      window.location.href = "/admin#sub=developer-portal";
      return;
    }
  } catch (e) {
    toast.error(e?.response?.data?.detail || `Could not switch to ${target}`);
  } finally {
    setBusy(null);
  }
}

// ----------------------------------------------------------------------------
// Public component
// ----------------------------------------------------------------------------
export function ModeSwitcher({ mode, compact = false, align = "end" }) {
  const { loading, can, roles } = useAvailableModes(mode);
  const [busy, setBusy] = useState(null);

  // v2.7.45 Phase-1 · Visibility hardening — never auto-hide the button.
  // Previously this returned null when the calling user only had a single
  // role, which left admins staring at an "App Home" pill with no way to
  // confirm they were in the admin view (and no entry into Rider/Driver
  // when a multi-role link gets restored). Now we ALWAYS render the
  // trigger; the dropdown menu shows the available targets (or a
  // "currently in X mode — no other modes linked" hint when there are
  // none).
  const hasAnyTarget = !!(can.admin || can.driver || can.rider || can.developer);

  const trigger = (
    <Button
      variant="outline"
      size="sm"
      disabled={loading}
      data-testid="mode-switcher-trigger"
      // v2.7.45 Phase-1 (rev) · Mirror the "App Home" pill exactly so the
      // top-bar control set reads as a single visual cluster. White
      // outline pill with deep slate copy in light mode; same zinc-950
      // outline pill in dark mode. The ArrowLeftRight icon picks up an
      // emerald accent so the affordance remains obvious without the
      // previous solid-block treatment.
      className="bg-white border-slate-300 text-slate-800 hover:bg-slate-100 hover:text-slate-900 dark:bg-zinc-950 dark:border-zinc-700 dark:text-zinc-300 dark:hover:text-amber-400 h-8 text-xs gap-1 font-semibold"
      title="Switch mode (Admin · Driver · Rider · Developer)"
    >
      {loading
        ? <Loader2 className="h-3 w-3 animate-spin text-emerald-600 dark:text-emerald-300"/>
        : <ArrowLeftRight className="h-3 w-3 text-emerald-600 dark:text-emerald-300"/>}
      {!compact && <span>Switch Mode</span>}
      <ChevronDown className="h-3 w-3 opacity-70"/>
    </Button>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="bg-zinc-950 border-zinc-800 text-zinc-200 min-w-[220px]" data-testid="mode-switcher-menu">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-emerald-400">
          Switch context
        </DropdownMenuLabel>
        <div className="px-2 pb-1 text-[10px] text-zinc-500">
          Currently in <span className="font-bold uppercase text-zinc-300">{mode}</span> mode
          {roles?.length ? ` · ${roles.length} role${roles.length === 1 ? "" : "s"}` : ""}
        </div>
        <DropdownMenuSeparator className="bg-zinc-800"/>
        {!hasAnyTarget && !loading && (
          <div className="px-3 py-2 text-[11px] text-zinc-400" data-testid="mode-switch-no-targets">
            No other modes linked to this account.
          </div>
        )}
        {can.admin && (
          <DropdownMenuItem
            data-testid="mode-switch-admin"
            disabled={busy !== null}
            onClick={() => jumpTo(mode, "admin", setBusy)}
            className="cursor-pointer focus:bg-amber-400/10 focus:text-amber-300"
          >
            {busy === "admin" ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin"/> : <Shield className="h-3.5 w-3.5 mr-2 text-amber-400"/>}
            Admin Console
            <span className="ml-auto text-[10px] text-zinc-500">/admin</span>
          </DropdownMenuItem>
        )}
        {can.driver && (
          <DropdownMenuItem
            data-testid="mode-switch-driver"
            disabled={busy !== null}
            onClick={() => jumpTo(mode, "driver", setBusy)}
            className="cursor-pointer focus:bg-amber-400/10 focus:text-amber-300"
          >
            {busy === "driver" ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin"/> : <Car className="h-3.5 w-3.5 mr-2 text-amber-400"/>}
            Driver App
            <span className="ml-auto text-[10px] text-zinc-500">/dashboard</span>
          </DropdownMenuItem>
        )}
        {can.rider && (
          <DropdownMenuItem
            data-testid="mode-switch-rider"
            disabled={busy !== null}
            onClick={() => jumpTo(mode, "rider", setBusy)}
            className="cursor-pointer focus:bg-blue-400/10 focus:text-blue-300"
          >
            {busy === "rider" ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin"/> : <User className="h-3.5 w-3.5 mr-2 text-blue-300"/>}
            Rider Portal
            <span className="ml-auto text-[10px] text-zinc-500">/ride</span>
          </DropdownMenuItem>
        )}
        {can.developer && (
          <>
            <DropdownMenuSeparator className="bg-zinc-800"/>
            <DropdownMenuItem
              data-testid="mode-switch-developer"
              disabled={busy !== null}
              onClick={() => jumpTo(mode, "developer", setBusy)}
              className="cursor-pointer focus:bg-emerald-400/10 focus:text-emerald-300"
            >
              <Code2 className="h-3.5 w-3.5 mr-2 text-emerald-300"/>
              Developer Area
              <span className="ml-auto text-[10px] text-zinc-500">/admin · dev</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ModeSwitcher;
